import { Grid } from "@mui/material";
import {
  clearAllFilters,
  setInnCode,
  setSelectedBrand,
  setSelectedFunctionalAreas,
  setSelectedInncodes,
  setSelectedPlanSources,
  setSelectedStatus,
  setSelectedUsers
} from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import ButtonLink from "components/common/Link";
import SingleSelectFilter from "components/common/SingleSelectFilter";
import Typography from "components/common/Typography";
import MultipleSelectCheckmarks from "components/common/dropdowns/MultiSelect";
import { isEmpty, uniq, uniqWith } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import {
  DEFAULT_CORP_INNCODE,
  USER_PAGE_SIZE,
  minSearchStrLength,
  planSourceList,
  statusTypes
} from "utils/CommonConstants";
import {
  adminInncodeNameToId,
  brandCodesToBrandNames,
  brandNameToBrandCode,
  converter_ArrayString,
  functionalAreaIdsToNames,
  functionalAreaNamesToIds,
  getKeyByValue,
  getSourceName,
  useDebouncedEffect,
  userNameToHltId
} from "utils/CommonFunctions";
import { clearUserDetail, fetchAutoCompleteAsync } from "../MyPlanDetails/MyPlanDetails.slice";
import {
  clearAdminInncodes,
  fetchAdminInncodesAsync,
  setBookmarkedInncodes,
  setSelectedAdminInncodes
} from "./myplans.slice";

interface Props {
  filters: any;
  setFilters: any;
}

function Filters(props: Props) {
  const dispatch = useAppDispatch();
  const { filters, setFilters } = props;
  const [inputValue, setInputValue] = useState("");
  const { isMobileScreen } = useContext(ThemeContext);
  const [userInputValue, setUserInputValue] = useState("");
  const isAdmin = useAppSelector((state) => state.app.isAdmin);
  const [adminInncodeOptions, setAdminInncodeOptions] = useState<any>([]);
  const userDetail = useAppSelector((state) => state.myPlanDetails.userDetail);
  const isUserDetailLoading: any = useAppSelector((state) => state.myPlanDetails.isUserDetailLoading);

  const functionalAreas: any = useAppSelector((state) => state.functionalAreas?.list);
  const selectedAdminInncodes = useAppSelector((state) => state.myplans.MyPlans.selectedAdminInncodes);
  const { innCodes, adminInncodes, brands, innCodeDetails, bookMarkInncodes }: any = useAppSelector(
    (state) => state.myplans.MyPlans
  );
  const { selectedFunctionalAreas, selectedInncodes, selectedBrand, selectedStatus, selectedUser } = useAppSelector(
    (state: any) => state.app
  );
  const brandsArray = brands.filter((element: any) => !!element?.brandName);
  const [selectedArr, setSelectedArr] = useState<string[]>(innCodeDetails);
  const [bookmarked, setBookmarked] = useState<string[]>(bookMarkInncodes);
  const [clearAdminInncodeList, setClearAdminInncodeList] = useState(false);
  const [selectedInncodeArr, setSelectedInncodeArr] = useState<any>([]);

  useEffect(() => {
    let arr = [];
    if (bookmarked.length) {
      arr = [...bookmarked, ...selectedArr, ...adminInncodes, ...selectedAdminInncodes];
    } else {
      arr = [...bookMarkInncodes, ...selectedArr, ...adminInncodes, ...selectedAdminInncodes];
    }

    const inncodeOptions = uniqWith(arr, function (arrVal, othVal) {
      return arrVal?.innCode === othVal?.innCode;
    });

    if (inncodeOptions?.length > 0) {
      setAdminInncodeOptions(inncodeOptions);
    } else {
      setAdminInncodeOptions([
        { id: "NA", innCode: "No Bookmarks Selected", name: "Search an Inncode", isDisabled: true }
      ]);
    }
  }, [adminInncodes, bookMarkInncodes]);

  useDebouncedEffect(
    () => {
      if (inputValue && inputValue.length > minSearchStrLength) {
        dispatch(fetchAdminInncodesAsync(inputValue));
      }
      if (!inputValue.length) dispatch(clearAdminInncodes());
    },
    [inputValue],
    500
  );

  useDebouncedEffect(
    () => {
      if (userInputValue && userInputValue.length > minSearchStrLength) {
        const innCodes = adminInncodeOptions.filter((a: any) => selectedInncodeArr.includes(a?.name));
        const data = {
          value: userInputValue,
          page: 0,
          size: USER_PAGE_SIZE,
          innCode: innCodes.map(({ innCode }: any) => innCode).join(",")
        };

        dispatch(fetchAutoCompleteAsync(data));
      }

      if (!userInputValue.length && !selectedUser) dispatch(clearUserDetail());
    },
    [userInputValue],
    500
  );

  const onMultiSelectChange = (value: any, name: any) => {
    if (name === "brandCode") {
      value = brandNameToBrandCode(value);
      dispatch(setSelectedBrand(value));
    }

    if (name === "functionalArea") {
      value = functionalAreaNamesToIds(value);
      dispatch(setSelectedFunctionalAreas(value));
    }

    if (name === "innCode") {
      if (isAdmin) {
        dispatch(setSelectedUsers(""));
        let selectedInncodes = value.map((item: any) => adminInncodeOptions.find((op: any) => op.name === item));
        dispatch(setSelectedAdminInncodes(selectedInncodes));
        value = adminInncodeNameToId(value, selectedArr);
        value = uniqWith(value, function (arrVal, othVal) {
          return arrVal === othVal;
        });
        setClearAdminInncodeList(false);
        dispatch(setBookmarkedInncodes(bookmarked));
        selectedInncodes.length === 1 ? dispatch(setInnCode(value[0])) : dispatch(setInnCode(DEFAULT_CORP_INNCODE));
      } else {
        dispatch(setSelectedInncodes(value));
      }
    }

    if (name === "status") {
      dispatch(setSelectedStatus(value));
    }

    if (name === "searchedHltId") {
      value = userNameToHltId([value], userDetail);
      dispatch(setSelectedUsers(value.length ? value[0] : ""));
    }

    if (name === "innCode") {
      setFilters({ ...filters, searchedHltId: [], [name]: value.join(",") });
    } else {
      setFilters({ ...filters, [name]: value.join(",") });
    }

    if (name === "planSources") {
      const list = value.map((item: any) => getKeyByValue(planSourceList, item));
      setFilters({ ...filters, planSources: uniq(list).join(",") });
      dispatch(setSelectedPlanSources(uniq(list).join(",")));
    }
  };

  const handleClose = () => {
    setInputValue("");
  };

  return (
    <>
      {Object.values(filters)
        ?.flat()
        .some((element: any) => element) ? (
        <Grid item xs={12} sm={1}>
          <ButtonLink
            text="Clear all"
            textVariant="body2"
            underline="none"
            sx={{ color: tokens.Text.General.Color }}
            onClick={() => {
              setFilters({
                innCode: [],
                functionalArea: [],
                status: [],
                brandCode: [],
                searchedHltId: [],
                planSources: []
              });
              setClearAdminInncodeList(true);
              dispatch(clearAllFilters());
            }}
          />
        </Grid>
      ) : (
        <>
          {!isMobileScreen && (
            <Typography variant={"body2"} sx={{ marginRight: "8px" }}>
              Filter by
            </Typography>
          )}
        </>
      )}
      {isAdmin && adminInncodeOptions?.length > 0 && (
        <MultipleSelectCheckmarks
          name="innCode"
          label="InnCode"
          options={adminInncodeOptions}
          onChange={onMultiSelectChange}
          inputValue={inputValue}
          isInputEnabled={isAdmin}
          handleInputChange={(e: any) => setInputValue(e.target.value)}
          handleClose={handleClose}
          bookmarked={bookmarked}
          setBookmarked={setBookmarked}
          setSelectedArr={setSelectedArr}
          clearAdminInncodeList={clearAdminInncodeList}
          setSelectedInncodeArr={setSelectedInncodeArr}
        />
      )}
      {!isAdmin && (
        <MultipleSelectCheckmarks
          minWidth={isMobileScreen ? "160px" : ""}
          name="innCode"
          label="Inncode"
          options={converter_ArrayString(innCodes, "name")}
          onChange={onMultiSelectChange}
          initialValues={selectedInncodes}
        />
      )}
      {isAdmin && (
        <SingleSelectFilter
          label="User"
          options={userDetail}
          optionKeyName="displayName"
          isLoading={isUserDetailLoading}
          inputValue={userInputValue}
          handleInputChange={(e: any) => setUserInputValue(e.target.value)}
          onSubmit={(val) => {
            onMultiSelectChange(val, "searchedHltId");
          }}
          selectedOption={userDetail.filter((o: any) => o.hltId === selectedUser)[0]?.displayName || ""}
        />
      )}
      <MultipleSelectCheckmarks
        minWidth={isMobileScreen ? "160px" : ""}
        name="brandCode"
        label="Brand"
        options={converter_ArrayString(
          brandsArray
            ?.sort((a: any, b: any) => a?.brandName.localeCompare(b?.brandName))
            .map((item: any) => item.brandName),
          "name"
        )}
        onChange={onMultiSelectChange}
        initialValues={brandCodesToBrandNames(selectedBrand)}
      />
      <MultipleSelectCheckmarks
        minWidth={isMobileScreen ? "160px" : "120px"}
        name="planSources"
        label="Source"
        options={converter_ArrayString(uniq(Object.values(planSourceList)), "name")}
        onChange={onMultiSelectChange}
        initialValues={
          !isEmpty(filters.planSources)
            ? uniq(filters.planSources.split(",").map((item: any) => getSourceName(item)))
            : []
        }
      />
      <MultipleSelectCheckmarks
        minWidth={isMobileScreen ? "160px" : "150px"}
        name="functionalArea"
        label="Functional Area"
        options={functionalAreas}
        initialValues={functionalAreaIdsToNames(selectedFunctionalAreas)}
        onChange={onMultiSelectChange}
      />
      <MultipleSelectCheckmarks
        minWidth={isMobileScreen ? "160px" : ""}
        name="status"
        label="Status"
        options={statusTypes.map((item) => {
          return { id: item, name: item };
        })}
        initialValues={selectedStatus}
        onChange={onMultiSelectChange}
      />
    </>
  );
}

export default Filters;
